import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import {
    FiCalendar,
    FiClock,
    FiMapPin,
    FiPlus,
    FiList,
    FiEdit2,
    FiTrash2,
    FiX,
    FiSave
} from "react-icons/fi";
import { format } from "date-fns";
import { pl, enUS } from "date-fns/locale";
import { CustomDatePicker, CustomTimePicker } from '../commons/datepicker';
import { TrainerContext } from "../../context/TrainerContext";

const GroupTrainings = ({ groupId }) => {
    const { t, i18n } = useTranslation(['groups', 'common']);
    const { trainer } = useContext(TrainerContext);
    const [trainings, setTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isBookModalOpen, setIsBookModalOpen] = useState(false);
    const [isMultipleBookModalOpen, setIsMultipleBookModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [halls, setHalls] = useState([]);
    const [currentTraining, setCurrentTraining] = useState(null);

    // Form states for single booking
    const [bookingForm, setBookingForm] = useState({
        hallId: "",
        date: "",
        startTime: "",
        endTime: "",
    });

    // Form states for multiple bookings
    const [multipleBookingForm, setMultipleBookingForm] = useState({
        hallId: "",
        timeSlots: [{ date: "", startTime: "", endTime: "" }]
    });

    // Edit form state
    const [editForm, setEditForm] = useState({
        hallId: "",
        date: "",
        startTime: "",
        endTime: "",
        notes: ""
    });

    useEffect(() => {
        fetchTrainings();
        fetchHalls();
    }, [groupId]);

    const fetchTrainings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/group-trainings/group/${groupId}/upcoming`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTrainings(response.data);
        } catch (err) {
            setError(t('trainings.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchHalls = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/school/${trainer.schoolId}/halls`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setHalls(response.data || []);

            // Set default hall if available
            if (response.data && response.data.length > 0) {
                setBookingForm(prev => ({ ...prev, hallId: response.data[0].id }));
                setMultipleBookingForm(prev => ({ ...prev, hallId: response.data[0].id }));
            }
        } catch (err) {
            console.error("Error fetching halls:", err);
            setHalls([]);
        }
    };

    // Helper to show notifications
    const showNotification = (message, type = "success") => {
        const notification = document.createElement("div");
        notification.className = type === "success" ? "success-notification" : "error-notification";
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 3000);
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const locale = i18n.language === 'pl' ? pl : enUS;
        return format(date, "d MMMM yyyy", { locale });
    };

    // Format time for display
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return format(date, "HH:mm");
    };

    // Calculate end time from scheduledAt + durationMinutes
    const getEndTime = (training) => {
        const startDate = new Date(training.scheduledAt);
        const endDate = new Date(startDate.getTime() + (training.durationMinutes || 60) * 60000);
        return format(endDate, "HH:mm");
    };

    // Handle single booking form changes
    const handleBookingFormChange = (e) => {
        const { name, value } = e.target;
        setBookingForm(prev => ({ ...prev, [name]: value }));
    };

    // Handle multiple booking form changes
    const handleMultipleBookingFormChange = (e) => {
        const { name, value } = e.target;
        setMultipleBookingForm(prev => ({ ...prev, [name]: value }));
    };

    // Handle time slot changes in multiple booking
    const handleTimeSlotChange = (index, field, value) => {
        const updatedSlots = [...multipleBookingForm.timeSlots];
        updatedSlots[index] = { ...updatedSlots[index], [field]: value };
        setMultipleBookingForm(prev => ({ ...prev, timeSlots: updatedSlots }));
    };

    // Add new time slot in multiple booking
    const addTimeSlot = () => {
        setMultipleBookingForm(prev => ({
            ...prev,
            timeSlots: [...prev.timeSlots, { date: "", startTime: "", endTime: "" }]
        }));
    };

    // Remove time slot in multiple booking
    const removeTimeSlot = (index) => {
        if (multipleBookingForm.timeSlots.length === 1) return; // Keep at least one slot

        const updatedSlots = multipleBookingForm.timeSlots.filter((_, i) => i !== index);
        setMultipleBookingForm(prev => ({ ...prev, timeSlots: updatedSlots }));
    };

    // Handle edit form changes
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    // Open edit modal and set current training
    const openEditModal = (training) => {
        const startDate = new Date(training.scheduledAt);
        // Calculate end time from scheduledAt + durationMinutes
        const endDate = new Date(startDate.getTime() + (training.durationMinutes || 60) * 60000);

        setCurrentTraining(training);
        setEditForm({
            hallId: training.hallId || halls.find(h => h.name === training.hallName)?.id || "",
            date: format(startDate, "yyyy-MM-dd"),
            startTime: format(startDate, "HH:mm"),
            endTime: format(endDate, "HH:mm"),
            notes: training.notes || ""
        });
        setIsEditModalOpen(true);
    };

    // Open delete confirmation modal
    const openDeleteModal = (training) => {
        setCurrentTraining(training);
        setIsDeleteModalOpen(true);
    };

    // Book a single training
    const bookSingleTraining = async () => {
        if (!bookingForm.date || !bookingForm.startTime || !bookingForm.endTime) {
            showNotification(t('trainings.dateAndTimesRequired'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            // Calculate duration in minutes from start and end time
            const startParts = bookingForm.startTime.split(':');
            const endParts = bookingForm.endTime.split(':');
            const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            const durationMinutes = endMinutes - startMinutes;

            if (durationMinutes < 15) {
                showNotification(t('trainings.minDuration'), "error");
                return;
            }

            const payload = {
                groupId: groupId,
                scheduledAt: `${bookingForm.date}T${bookingForm.startTime}:00`,
                durationMinutes: durationMinutes
            };

            // Add hallId to payload only if selected
            if (bookingForm.hallId) {
                payload.hallId = bookingForm.hallId;
            }

            await axios.post('/api/v1/group-trainings', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.booked'));
            setIsBookModalOpen(false);
            fetchTrainings();

            // Reset form
            setBookingForm({
                hallId: halls.length > 0 ? halls[0].id : "",
                date: "",
                startTime: "",
                endTime: "",
            });
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('trainings.bookError');
            showNotification(errorMsg, "error");
        }
    };

    // Book multiple trainings (one by one using single endpoint)
    const bookMultipleTrainings = async () => {
        // Validate form
        const isValid = multipleBookingForm.timeSlots.every(slot =>
            slot.date && slot.startTime && slot.endTime
        );

        if (!isValid) {
            showNotification(t('trainings.fillAllSlots'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            // Book each training individually
            for (const slot of multipleBookingForm.timeSlots) {
                const startParts = slot.startTime.split(':');
                const endParts = slot.endTime.split(':');
                const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
                const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
                const durationMinutes = endMinutes - startMinutes;

                const payload = {
                    groupId: groupId,
                    scheduledAt: `${slot.date}T${slot.startTime}:00`,
                    durationMinutes: durationMinutes
                };

                if (multipleBookingForm.hallId) {
                    payload.hallId = multipleBookingForm.hallId;
                }

                await axios.post('/api/v1/group-trainings', payload, { headers });
            }

            showNotification(t('trainings.multipleBooked'));
            setIsMultipleBookModalOpen(false);
            fetchTrainings();

            // Reset form
            setMultipleBookingForm({
                hallId: halls.length > 0 ? halls[0].id : "",
                timeSlots: [{ date: "", startTime: "", endTime: "" }]
            });
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('trainings.multipleBookError');
            showNotification(errorMsg, "error");
        }
    };

    // Update training
    const updateTraining = async () => {
        if (!editForm.date || !editForm.startTime || !editForm.endTime) {
            showNotification(t('trainings.dateAndTimesRequired'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            // Calculate duration in minutes from start and end time
            const startParts = editForm.startTime.split(':');
            const endParts = editForm.endTime.split(':');
            const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            const durationMinutes = endMinutes - startMinutes;

            if (durationMinutes < 15) {
                showNotification(t('trainings.minDuration'), "error");
                return;
            }

            const payload = {
                scheduledAt: `${editForm.date}T${editForm.startTime}:00`,
                durationMinutes: durationMinutes,
                notes: editForm.notes
            };

            // Add hallId to payload only if selected
            if (editForm.hallId) {
                payload.hallId = editForm.hallId;
            }

            await axios.put(`/api/v1/group-trainings/${currentTraining.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.updated'));
            setIsEditModalOpen(false);
            fetchTrainings();
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('trainings.updateError');
            showNotification(errorMsg, "error");
        }
    };

    // Delete training
    const deleteTraining = async () => {
        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/group-trainings/${currentTraining.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.deleted'));
            setIsDeleteModalOpen(false);
            fetchTrainings();
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('trainings.deleteError');
            showNotification(errorMsg, "error");
        }
    };

    return (
        <div className="modern-details-section">
            <h3 className="modern-section-title">
                <FiCalendar className="section-icon" />
                <span>{t('trainings.groupTrainings')}</span>
                <span className="modern-count">{trainings.length}</span>
            </h3>

            <div className="modern-training-actions">
                <button
                    className="modern-action-btn primary"
                    onClick={() => setIsBookModalOpen(true)}
                >
                    <FiPlus className="icon" />
                    <span>{t('trainings.bookTraining')}</span>
                </button>
                <button
                    className="modern-action-btn secondary"
                    onClick={() => setIsMultipleBookModalOpen(true)}
                >
                    <FiList className="icon" />
                    <span>{t('trainings.bookMultiple')}</span>
                </button>
            </div>

            {loading ? (
                <div className="modern-loading-indicator">
                    <div className="spinner-small"></div>
                    <span>{t('trainings.loadingTrainings')}</span>
                </div>
            ) : error ? (
                <div className="modern-error-message">{error}</div>
            ) : trainings.length === 0 ? (
                <div className="modern-empty-list">
                    {t('trainings.noUpcoming')}
                </div>
            ) : (
                <div className="modern-trainings-list">
                    {trainings.map((training) => (
                        <div key={training.id} className="modern-training-card">
                            <div className="training-header">
                                <div className="training-date">
                                    <FiCalendar />
                                    <span>{formatDate(training.scheduledAt)}</span>
                                </div>
                                <div className="training-actions">
                                    <button
                                        className="action-icon-btn"
                                        onClick={() => openEditModal(training)}
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        className="action-icon-btn delete"
                                        onClick={() => openDeleteModal(training)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>
                            <div className="training-info">
                                <div className="info-row">
                                    <FiClock className="icon" />
                                    <span>{formatTime(training.scheduledAt)} - {getEndTime(training)}</span>
                                </div>
                                {training.hallName && (
                                    <div className="info-row">
                                        <FiMapPin className="icon" />
                                        <span>{training.hallName}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Book Single Training Modal */}
            {isBookModalOpen && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.bookGroupTraining')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsBookModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="hallSelect">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="hallSelect"
                                        name="hallId"
                                        value={bookingForm.hallId}
                                        onChange={handleBookingFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="modern-input-group">
                                <label htmlFor="trainingDate">{t('trainings.date')}</label>
                                <CustomDatePicker
                                    id="trainingDate"
                                    name="date"
                                    value={bookingForm.date}
                                    onChange={handleBookingFormChange}
                                />
                            </div>

                            <div className="modern-time-inputs">
                                <div className="modern-input-group">
                                    <label htmlFor="startTime">{t('trainings.startTime')}</label>
                                    <CustomTimePicker
                                        id="startTime"
                                        name="startTime"
                                        value={bookingForm.startTime}
                                        onChange={handleBookingFormChange}
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="endTime">{t('trainings.endTime')}</label>
                                    <CustomTimePicker
                                        id="endTime"
                                        name="endTime"
                                        value={bookingForm.endTime}
                                        onChange={handleBookingFormChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsBookModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={bookSingleTraining}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.book')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Book Multiple Trainings Modal */}
            {isMultipleBookModalOpen && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal multiple-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.bookMultiple')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsMultipleBookModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="hallSelectMultiple">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="hallSelectMultiple"
                                        name="hallId"
                                        value={multipleBookingForm.hallId}
                                        onChange={handleMultipleBookingFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <h3 className="modern-slots-title">{t('trainings.trainingSlots')}</h3>

                            {multipleBookingForm.timeSlots.map((slot, index) => (
                                <div key={index} className="modern-time-slot">
                                    <div className="slot-header">
                                        <h4>{t('trainings.slot', { number: index + 1 })}</h4>
                                        {multipleBookingForm.timeSlots.length > 1 && (
                                            <button
                                                className="remove-slot-btn"
                                                onClick={() => removeTimeSlot(index)}
                                            >
                                                <FiX />
                                            </button>
                                        )}
                                    </div>

                                    <div className="modern-input-group">
                                        <label>{t('trainings.date')}</label>
                                        <CustomDatePicker
                                            name={`slot-date-${index}`}
                                            value={slot.date}
                                            onChange={(e) => handleTimeSlotChange(index, "date", e.target.value)}
                                        />
                                    </div>

                                    <div className="modern-time-inputs">
                                        <div className="modern-input-group">
                                            <label>{t('trainings.start')}</label>
                                            <CustomTimePicker
                                                name={`slot-start-${index}`}
                                                value={slot.startTime}
                                                onChange={(e) => handleTimeSlotChange(index, "startTime", e.target.value)}
                                            />
                                        </div>
                                        <div className="modern-input-group">
                                            <label>{t('trainings.end')}</label>
                                            <CustomTimePicker
                                                name={`slot-end-${index}`}
                                                value={slot.endTime}
                                                onChange={(e) => handleTimeSlotChange(index, "endTime", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                className="modern-add-slot-btn"
                                onClick={addTimeSlot}
                            >
                                <FiPlus /> {t('trainings.addSlot')}
                            </button>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsMultipleBookModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={bookMultipleTrainings}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.bookTrainings')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Training Modal */}
            {isEditModalOpen && currentTraining && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.editTraining')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="editHallSelect">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="editHallSelect"
                                        name="hallId"
                                        value={editForm.hallId}
                                        onChange={handleEditFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="modern-input-group">
                                <label htmlFor="editDate">{t('trainings.date')}</label>
                                <CustomDatePicker
                                    id="editDate"
                                    name="date"
                                    value={editForm.date}
                                    onChange={handleEditFormChange}
                                />
                            </div>

                            <div className="modern-time-inputs">
                                <div className="modern-input-group">
                                    <label htmlFor="editStartTime">{t('trainings.startTime')}</label>
                                    <CustomTimePicker
                                        id="editStartTime"
                                        name="startTime"
                                        value={editForm.startTime}
                                        onChange={handleEditFormChange}
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="editEndTime">{t('trainings.endTime')}</label>
                                    <CustomTimePicker
                                        id="editEndTime"
                                        name="endTime"
                                        value={editForm.endTime}
                                        onChange={handleEditFormChange}
                                    />
                                </div>
                            </div>

                            <div className="modern-input-group">
                                <label htmlFor="editNotes">{t('trainings.notesOptional')}</label>
                                <textarea
                                    id="editNotes"
                                    name="notes"
                                    value={editForm.notes}
                                    onChange={handleEditFormChange}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={updateTraining}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.saveChanges')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && currentTraining && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal confirmation-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.confirmDelete')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsDeleteModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            <p className="confirmation-message">
                                {t('trainings.confirmDeleteMessage', { date: formatDate(currentTraining.scheduledAt), time: formatTime(currentTraining.scheduledAt) })}
                            </p>
                            <p className="warning-message">
                                {t('trainings.irreversible')}
                            </p>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsDeleteModalOpen(false)}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="modern-delete-btn"
                                onClick={deleteTraining}
                            >
                                {t('trainings.deleteTraining')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupTrainings;
